// aluno/lgpd-export.js — Exportação de dados do titular (LGPD Art. 18 II).
//
// H6 — Responsável por:
//   - Renderizar a seção "Baixar meus dados" na página /consentimentos
//   - Chamar api.exportarMeusDados e baixar .json + .txt legível
//
// Contrato público:
//   renderExportSection() → string HTML
//   bindExportEvents(container, setStatus) → void
//   startExport(statusEl, setStatus) → Promise<void>

import { api } from '../core/api.js';
import { auth } from '../core/auth.js';

export function renderExportSection() {
  return `
    <section class="lgpd-export-box" aria-labelledby="lgpd-export-title">
      <h2 class="lgpd-export-title" id="lgpd-export-title">Baixar meus dados</h2>
      <p class="lgpd-export-desc">
        Você pode pedir, a qualquer momento, uma cópia de <strong>todos os dados pessoais</strong>
        que a Eloquênce mantém sobre você (<em>Art. 18, II e IV da LGPD</em>).
        Serão baixados dois arquivos: um .json (para portabilidade) e um .txt legível.
      </p>
      <div class="lgpd-consent-status-banner" role="status" aria-live="polite" data-export-status hidden></div>
      <button type="button" class="lgpd-export-btn" data-export-action>
        <span class="lgpd-export-btn-icon" aria-hidden="true">↓</span>
        Baixar meus dados
      </button>
    </section>
  `;
}

export function bindExportEvents(container, setStatus) {
  const btn = container.querySelector('[data-export-action]');
  const statusEl = container.querySelector('[data-export-status]');
  if (!btn) return;
  btn.addEventListener('click', async () => {
    btn.disabled = true;
    try {
      await startExport(statusEl, setStatus);
    } finally {
      btn.disabled = false;
    }
  });
}

export async function startExport(statusEl, setStatus) {
  const current = auth.getCurrent?.();
  const idToken = current?.idToken;
  if (!idToken) {
    setStatus(statusEl, 'Você precisa estar logado para exportar seus dados. Faça login e tente de novo.', 'warn');
    return;
  }

  setStatus(statusEl, 'Gerando seu arquivo de dados... isso pode levar até 15 segundos.', 'info');

  const result = await api.exportarMeusDados({ idToken, uaHash: _uaHashCurto() });
  if (!result.ok) {
    setStatus(statusEl, `Não conseguimos gerar o arquivo agora. Tente de novo em alguns instantes ou escreva para contato@eloquence.com.br (ref: ${result.error || 'erro'}).`, 'warn');
    return;
  }

  try {
    const payload = result.data;
    const dataStr = _hojeISO();
    const slug = _slugEmail(payload?.titular?.email || 'titular');
    const baseName = `meus-dados-eloquence-${slug}-${dataStr}`;

    // 1) JSON cru (portabilidade — Art. 18 V)
    _baixarBlob(
      new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' }),
      `${baseName}.json`
    );

    // 2) TXT legível (acesso — Art. 18 II) — facilita leitura humana
    _baixarBlob(
      new Blob([_renderTxt(payload)], { type: 'text/plain;charset=utf-8' }),
      `${baseName}.txt`
    );

    setStatus(statusEl, 'Pronto. Dois arquivos foram baixados (.json e .txt). Guarde em local seguro.', 'ok');
  } catch (err) {
    console.error('[lgpd-export] erro ao montar download:', err);
    setStatus(statusEl, 'Arquivo recebido, mas houve erro ao salvar. Tente de novo.', 'warn');
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function _baixarBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 200);
}

function _hojeISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function _slugEmail(email) {
  return String(email).toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
}

function _uaHashCurto() {
  const ua = (navigator?.userAgent || '').slice(0, 200);
  let h = 0;
  for (let i = 0; i < ua.length; i++) {
    h = ((h << 5) - h + ua.charCodeAt(i)) | 0;
  }
  return 'ua_' + (h >>> 0).toString(36);
}

/**
 * Renderiza o payload em texto legível para humanos.
 * Não escapa HTML — saída é .txt puro.
 */
function _renderTxt(p) {
  if (!p) return 'Arquivo vazio.';
  const L = [];
  L.push('═══════════════════════════════════════════════════════════════');
  L.push('   MEUS DADOS PESSOAIS NA ELOQUENCE');
  L.push('   Exportação LGPD — Art. 18, II e IV');
  L.push('═══════════════════════════════════════════════════════════════');
  L.push('');
  L.push(`Gerado em: ${p.gerado_em || ''}`);
  L.push('');

  if (p.titular) {
    L.push('── TITULAR ────────────────────────────────────────────────');
    L.push(`Nome:    ${p.titular.nome || '-'}`);
    L.push(`Email:   ${p.titular.email || '-'}`);
    L.push(`CPF:     ${_fmtCpf(p.titular.cpf)}`);
    L.push(`Turma:   ${p.titular.turma || '-'}`);
    L.push(`Papel:   ${p.titular.role || '-'}`);
    L.push('');
  }

  if (p.dados_pessoais) {
    L.push('── DADOS DA MATRÍCULA (FICHA LIMPA) ───────────────────────');
    const d = p.dados_pessoais;
    L.push(`Nome completo:    ${d.nome_completo || '-'}`);
    L.push(`Data matrícula:   ${d.data_matricula || '-'}`);
    L.push(`Email:            ${d.email || '-'}`);
    L.push(`CPF:              ${_fmtCpf(d.cpf)}`);
    L.push(`RG:               ${d.rg || '-'}`);
    L.push(`Telefone:         ${d.telefone || '-'}`);
    L.push(`Turma:            ${d.turma || '-'}`);
    L.push('');
  }

  if (Array.isArray(p.frequencia) && p.frequencia.length) {
    L.push(`── FREQUÊNCIA (${p.frequencia.length} registros) ─────────────────────`);
    p.frequencia.forEach(f => {
      L.push(`  ${f.data || '?'}  | ${f.modulo || '-'} | ${f.materia || '-'} | ${f.status || '-'} | turma ${f.turma || '-'}`);
    });
    L.push('');
  }

  if (Array.isArray(p.consentimentos) && p.consentimentos.length) {
    L.push(`── HISTÓRICO DE CONSENTIMENTOS (${p.consentimentos.length} eventos) ───`);
    p.consentimentos.forEach(c => {
      const fin = c.finalidades || {};
      L.push(`  ${c.timestamp || '?'} | Termo v${c.termo_version} | ${c.evento}`);
      L.push(`     execucao_curso=${fin.execucao_curso ? 'sim' : 'não'}, registro_pedagogico=${fin.registro_pedagogico ? 'sim' : 'não'},`);
      L.push(`     marketing=${fin.marketing ? 'sim' : 'não'}, imagem_voz=${fin.imagem_voz ? 'sim' : 'não'}, depoimentos=${fin.depoimentos ? 'sim' : 'não'}`);
    });
    L.push('');
  }

  if (Array.isArray(p.tentativas_auth) && p.tentativas_auth.length) {
    L.push(`── TENTATIVAS DE LOGIN (últimas ${p.tentativas_auth.length}) ────────────`);
    p.tentativas_auth.forEach(t => {
      L.push(`  ${t.timestamp || '?'} | ${t.resultado} | role=${t.role || '-'} | erro=${t.erro || '-'}`);
    });
    L.push('');
  }

  if (p.meta) {
    L.push('── CONTROLADOR ────────────────────────────────────────────');
    L.push(`${p.meta.controlador || ''}`);
    L.push(`CNPJ: ${p.meta.cnpj || ''}`);
    L.push(`Endereço: ${p.meta.endereco || ''}`);
    L.push('');
    L.push(`DPO: ${p.meta.dpo || ''}`);
    L.push(`Canal do titular: ${p.meta.canal_titular || ''}`);
    L.push(`Base legal: ${p.meta.base_legal_exportacao || ''}`);
    L.push(`Prazo de atendimento: ${p.meta.prazo_atendimento || ''}`);
    L.push('');
    L.push(p.meta.finalidade_registro || '');
  }

  L.push('');
  L.push('═══════════════════════════════════════════════════════════════');
  return L.join('\n');
}

function _fmtCpf(cpf) {
  const s = String(cpf || '').replace(/\D/g, '');
  if (s.length !== 11) return s || '-';
  return `${s.slice(0, 3)}.${s.slice(3, 6)}.${s.slice(6, 9)}-${s.slice(9)}`;
}
